/**
 * Wrappers for the embed-key endpoints (Pydantic schemas in
 * `src/builder/schemas/embed_keys.py`, track B3).
 *
 * The plaintext key crosses this boundary EXACTLY ONCE — on the response
 * of `issueEmbedKey`. It is never stored client-side and never
 * retrievable again; the listing returns `key_prefix` only. Everything in
 * the UI after issuance is keyed off `id` / `key_prefix`.
 */

import { api } from "@/lib/api";

/** Ceiling enforced server-side (`MAX_TOOL_ITERATIONS_CEILING`). */
export const MAX_TOOL_ITERATIONS = 8;

/**
 * Safe-by-default quota the form pre-fills. Mirrors the backend's
 * `DEFAULT_DAILY_MESSAGE_QUOTA`; `null` means "no quota", which the
 * operator has to choose explicitly.
 */
export const DEFAULT_DAILY_MESSAGE_QUOTA = 500;

export interface EmbedKey {
  id: string;
  label: string;
  /** First 8 chars of the SECRET half — disambiguation only. */
  key_prefix: string;
  project_slug: string;
  client_key: string;
  chat_context_key: string;
  allowed_origins: string[];
  enabled: boolean;
  allow_free_sql: boolean;
  max_tool_iterations: number;
  daily_message_quota: number | null;
  quota_day: string | null;
  quota_day_count: number;
  use_count: number;
  last_used_at: string | null;
  created_at: string;
  revoked_at: string | null;
}

/** Issuance response — the ONLY shape that ever carries the plaintext. */
export interface EmbedKeyIssued extends EmbedKey {
  key_plaintext: string;
  snippet_html: string;
}

export interface EmbedKeyListResponse {
  items: EmbedKey[];
  total: number;
}

export interface EmbedKeyCreatePayload {
  label: string;
  client_key: string;
  chat_context_key: string;
  /** Exact `scheme://host[:port]` values. At least one; no wildcards. */
  allowed_origins: string[];
  allow_free_sql?: boolean;
  max_tool_iterations?: number;
  /** `null` = no quota (explicit choice). Omit to take the backend default. */
  daily_message_quota?: number | null;
}

export interface EmbedKeyUpdatePayload {
  label?: string;
  allowed_origins?: string[];
  allow_free_sql?: boolean;
  max_tool_iterations?: number;
  daily_message_quota?: number;
  /**
   * Removing a quota must be EXPLICIT — an omitted
   * `daily_message_quota` leaves it untouched server-side, so a partial
   * update can never silently turn metering off.
   */
  clear_daily_message_quota?: boolean;
}

const projectBase = (slug: string) =>
  `/api/builder/projects/${encodeURIComponent(slug)}/embed-keys`;
const keyBase = (keyId: string) =>
  `/api/builder/embed-keys/${encodeURIComponent(keyId)}`;

export const embedApi = {
  list: (slug: string) => api.get<EmbedKeyListResponse>(projectBase(slug)),

  /** Returns the plaintext + paste-ready snippet. Shown once, never again. */
  issue: (slug: string, payload: EmbedKeyCreatePayload) =>
    api.post<EmbedKeyIssued>(projectBase(slug), payload),

  /** Policy edit only — never re-issues the secret. */
  update: (keyId: string, payload: EmbedKeyUpdatePayload) =>
    api.patch<EmbedKey>(keyBase(keyId), payload),

  /** Soft revoke: `enabled=false` + `revoked_at=now`. Keeps `use_count`. */
  revoke: (keyId: string) => api.delete<void>(keyBase(keyId)),
};

/**
 * Client-side origin check that MIRRORS the server's `normalize_origin`.
 *
 * It exists to give immediate feedback in the chip editor, NOT as the
 * gate — the server re-validates and rejects loudly. Returns the
 * normalized value, or `null` when the input is not an exact origin.
 */
export function normalizeOrigin(value: string): string | null {
  const raw = value.trim();
  if (!raw) return null;
  if (raw.toLowerCase() === "*" || raw.toLowerCase() === "null") return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  // A trailing "/" counts as a path — URL normalizes it to "/", so the
  // check is "anything beyond the bare origin".
  if (url.pathname !== "/" && url.pathname !== "") return null;
  if (url.search || url.hash || url.username || url.password) return null;
  if (!url.hostname) return null;
  // URL already strips the default port for the scheme.
  return url.port
    ? `${url.protocol}//${url.hostname.toLowerCase()}:${url.port}`
    : `${url.protocol}//${url.hostname.toLowerCase()}`;
}
